import React, { useEffect, useState } from "react";
import AddMenu from "./components/Modals/AddMenu";
import { useAppServices } from "../../../../hook/services";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const SettingMenu = () => {
  const AppService = useAppServices();
  const { companyId, type, customizationId } = useParams();
  const [settingMenus, setSettingMenus] = useState([]);
  const [data, setData] = useState({});
  const [loader, setLoader] = useState(true);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const query =
    type === "location" || type === "agency"
      ? `companyId=${companyId}&type=${type}`
      : `_id=${customizationId}`;

  const getData = async () => {
    setLoader(true);
    try {
      const { response } = await handler.filter({ query });
      if (response?.data) {
        setSettingMenus(response.data.setting_menus || []);
        setData(response.data);
      } else {
        setSettingMenus([]);
        setData({});
      }
    } catch (error) {
      console.error("Failed to fetch setting menus:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleDelete = async (editIndex) => {
    if (editIndex === null || editIndex < 0 || !data?._id) {
      toast.error("Invalid delete request.");
      return;
    }

    const setting_menus = data?.setting_menus ? [...data.setting_menus] : [];
    setting_menus.splice(editIndex, 1);

    const payload = {
      setting_menus,
      companyId,
      _id: data._id,
    };

    try {
      setDeletingIndex(editIndex);
      const { response } = await handler.update({ payload });
      if (response) {
        toast.success("Menu deleted.");
        getData();
      } else {
        toast.error("Failed to delete the menu.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting.");
    } finally {
      setDeletingIndex(null);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const items = Array.from(settingMenus);
    const [movedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, movedItem);

    const updatedMenus = items.map((menu, index) => {
      if (index === 0) {
        return {
          ...menu,
          index,
          menuPlacement: "before",
          menuTarget: null,
        };
      }
      return {
        ...menu,
        index,
        menuPlacement: "after",
        menuTarget: items[index - 1].id,
      };
    });

    setSettingMenus(updatedMenus);

    const payload = {
      _id: data._id,
      companyId,
      setting_menus: updatedMenus,
    };

    try {
      const { response } = await handler.update({ payload });
      if (response) {
        toast.success("Menu order updated.");
      } else {
        toast.error("Failed to update order.");
        getData();
      }
    } catch (error) {
      console.error("Reorder error:", error);
      toast.error("Failed to update order.");
      getData();
    }
  };

  return (
    <div className="sm-list-wrapper">
      <style>{`
        .sm-list-wrapper {
          background: #ffffff;
          border-radius: 16px;
          padding: 22px 24px;
          border: 1px solid #e2e8f0;
        }
        .sm-list-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }
        .sm-list-title {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sm-list-title .sm-badge {
          background: #eef2ff;
          color: #4338ca;
          font-size: 12px;
          font-weight: 600;
          border-radius: 999px;
          padding: 2px 10px;
        }
        .sm-list-subtitle {
          color: #64748b;
          font-size: 13px;
          margin: 4px 0 0;
        }
        .sm-empty {
          border: 1px dashed #cbd5e1;
          border-radius: 14px;
          padding: 48px 20px;
          text-align: center;
          background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
        }
        .sm-empty .sm-empty-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #4f46e5;
          font-size: 26px;
          box-shadow: 0 8px 24px rgba(79, 70, 229, 0.18);
          margin-bottom: 14px;
        }
        .sm-empty h6 {
          font-size: 15px;
          color: #0f172a;
          font-weight: 600;
          margin: 0 0 4px;
        }
        .sm-empty p {
          color: #64748b;
          font-size: 13px;
          margin: 0 0 14px;
        }
        .sm-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 0;
          margin: 0;
          list-style: none;
        }
        .sm-list-helper {
          font-size: 12px;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
        }
        .sm-row {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px 14px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: border-color 0.15s, box-shadow 0.15s, transform 0.05s;
        }
        .sm-row:hover {
          border-color: #c7d2fe;
        }
        .sm-row.is-dragging {
          border-color: #4f46e5;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
          background: #fff;
        }
        .sm-row.is-divider {
          background: linear-gradient(90deg, #f8fafc 0%, #eef2ff 100%);
          border-style: dashed;
        }
        .sm-grip {
          color: #94a3b8;
          font-size: 18px;
          cursor: grab;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          flex-shrink: 0;
        }
        .sm-grip:active { cursor: grabbing; }
        .sm-type-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          padding: 3px 9px;
          border-radius: 999px;
          font-weight: 600;
          flex-shrink: 0;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .sm-type-pill.divider { background: #fef3c7; color: #92400e; }
        .sm-type-pill.menu { background: #dcfce7; color: #166534; }
        .sm-row-body {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .sm-row-name {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
          word-break: break-word;
        }
        .sm-row-link {
          font-size: 12px;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
          word-break: break-all;
          margin: 0;
        }
        .sm-row-link .text-truncate {
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        .sm-row-id {
          font-size: 11px;
          color: #94a3b8;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .sm-row-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }
        .sm-row-actions .sm-action-btn {
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #475569;
          border-radius: 8px;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.15s;
        }
        .sm-row-actions .sm-action-btn.delete {
          color: #b91c1c;
          border-color: #fecaca;
          background: #fef2f2;
        }
        .sm-row-actions .sm-action-btn.delete:hover {
          background: #fee2e2;
          color: #991b1b;
        }
        .sm-skeleton {
          height: 56px;
          border-radius: 12px;
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: smSkeleton 1.5s ease-in-out infinite;
        }
        @keyframes smSkeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div className="sm-list-header">
        <div>
          <h3 className="sm-list-title">
            <i className="bi bi-list-ul text-primary" />
            Settings Menu
            {!loader && <span className="sm-badge">{settingMenus.length}</span>}
          </h3>
          <p className="sm-list-subtitle">
            Custom items added to the GHL settings sidebar — section headers
            and clickable links.
          </p>
        </div>
        {!loader && <AddMenu data={data} handleRefresh={getData} />}
      </div>

      {loader ? (
        <div className="sm-list">
          <div className="sm-skeleton" />
          <div className="sm-skeleton" />
          <div className="sm-skeleton" />
          <div className="sm-skeleton" />
        </div>
      ) : settingMenus.length === 0 ? (
        <div className="sm-empty">
          <div className="sm-empty-icon">
            <i className="bi bi-list-ul" />
          </div>
          <h6>No setting menu items yet</h6>
          <p>
            Add a section header or a custom link to extend the GHL settings
            sidebar.
          </p>
          <AddMenu data={data} handleRefresh={getData} />
        </div>
      ) : (
        <>
          <div className="sm-list-helper">
            <i className="bi bi-arrows-move" />
            Drag the handle on the left to reorder items.
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="settingMenus">
              {(provided) => (
                <ul
                  className="sm-list"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {settingMenus.map((item, index) => {
                    const isDivider = item.type === "Divider";
                    return (
                      <Draggable
                        key={index}
                        draggableId={`menu-${index}`}
                        index={index}
                      >
                        {(prov, snapshot) => (
                          <li
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            className={`sm-row ${
                              snapshot.isDragging ? "is-dragging" : ""
                            } ${isDivider ? "is-divider" : ""}`}
                            title={item.name}
                          >
                            <span
                              className="sm-grip"
                              {...prov.dragHandleProps}
                              title="Drag to reorder"
                            >
                              <i className="bi bi-grip-vertical" />
                            </span>

                            <span
                              className={`sm-type-pill ${
                                isDivider ? "divider" : "menu"
                              }`}
                            >
                              <i
                                className={`bi ${
                                  isDivider ? "bi-hr" : "bi-link-45deg"
                                }`}
                              />
                              {isDivider ? "Divider" : "Menu"}
                            </span>

                            <div className="sm-row-body">
                              <p className="sm-row-name">
                                {item.name || (
                                  <span className="text-muted fst-italic">
                                    Untitled
                                  </span>
                                )}
                              </p>
                              {!isDivider && item.link && (
                                <p className="sm-row-link">
                                  <i className="bi bi-globe" />
                                  <span
                                    className="text-truncate"
                                    title={item.link}
                                  >
                                    {item.link}
                                  </span>
                                </p>
                              )}
                              {item.id && (
                                <span className="sm-row-id">#{item.id}</span>
                              )}
                            </div>

                            <div className="sm-row-actions">
                              <AddMenu
                                data={data}
                                handleRefresh={getData}
                                selectedButton={item}
                                editIndex={index}
                              />
                              <button
                                className="sm-action-btn delete"
                                title="Delete"
                                onClick={() => handleDelete(index)}
                                disabled={deletingIndex === index}
                              >
                                {deletingIndex === index ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <>
                                    <i className="bi bi-trash" />
                                    Delete
                                  </>
                                )}
                              </button>
                            </div>
                          </li>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}
    </div>
  );
};

export default SettingMenu;
